import { useFetch } from '@/hooks/useFetch'
import { usePageMeta } from '@/hooks/usePageMeta'
import { Settings, TeamMember, Article, Document, PaginatedResponse } from '@/types'
import Hero from './components/Hero'
import FeaturedTeam from './components/FeaturedTeam'
import LatestNews from './components/LatestNews'
import FeaturedDocuments from './components/FeaturedDocuments'
import Loading from '@/components/common/Loading'
import ErrorMessage from '@/components/common/ErrorMessage'
import './Home.css'

function Home() {
  // Fetch settings for hero section
  const { data: settings, loading: settingsLoading, error: settingsError } =
    useFetch<Settings>('/settings')

  // Fetch featured team members
  const { data: teamData, loading: teamLoading, error: teamError } =
    useFetch<TeamMember[]>('/team?featured=1')

  // Fetch latest articles
  const { data: articlesResponse, loading: articlesLoading, error: articlesError } =
    useFetch<PaginatedResponse<Article>>('/articles?limit=3')

  // Fetch featured documents
  const { data: documentsData, loading: documentsLoading, error: documentsError } =
    useFetch<Document[]>('/documents?featured=1')

  usePageMeta({
    title: settings?.candidate_name || 'Avenir Théopolitain 2026',
    description: settings?.meta_description || settings?.slogan || undefined,
  })

  // Show global loading state
  if (settingsLoading || teamLoading || articlesLoading || documentsLoading) {
    return <Loading />
  }

  // Show global error if settings fail (critical)
  if (settingsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorMessage message="Erreur lors du chargement de la page" />
      </div>
    )
  }

  return (
    <div className="home-page">
      {/* Hero Section - Always show, use defaults if settings null */}
      <Hero
        settings={
          settings || {
            candidate_name: 'Avenir Théopolitain 2026',
            slogan: 'Pour un avenir meilleur',
            contact_email: '',
          }
        }
      />

      {/* Featured Team Section */}
      {teamError && (
        <div className="container mx-auto py-8">
          <ErrorMessage message="Erreur lors du chargement de l'équipe" />
        </div>
      )}
      {!teamError && teamData && teamData.length > 0 && (
        <FeaturedTeam teamMembers={teamData} />
      )}
      {!teamError && teamData && teamData.length === 0 && (
        <div className="container mx-auto py-16 text-center text-gray-500">
          <p>Aucun membre de l'équipe mis en avant pour le moment.</p>
        </div>
      )}

      {/* Latest News Section */}
      {articlesError && (
        <div className="container mx-auto py-8">
          <ErrorMessage message="Erreur lors du chargement des actualités" />
        </div>
      )}
      {!articlesError && articlesResponse?.data && articlesResponse.data.length > 0 && (
        <LatestNews articles={articlesResponse.data} />
      )}
      {!articlesError && articlesResponse?.data && articlesResponse.data.length === 0 && (
        <div className="container mx-auto py-16 text-center text-gray-500">
          <p>Aucune actualité pour le moment.</p>
        </div>
      )}

      {/* Featured Documents Section */}
      {documentsError && (
        <div className="container mx-auto py-8">
          <ErrorMessage message="Erreur lors du chargement des documents" />
        </div>
      )}
      {!documentsError && documentsData && documentsData.length > 0 && (
        <FeaturedDocuments documents={documentsData} />
      )}
      {!documentsError && documentsData && documentsData.length === 0 && (
        <div className="container mx-auto py-16 text-center text-gray-500">
          <p>Aucun document mis en avant pour le moment.</p>
        </div>
      )}
    </div>
  )
}

export default Home
